import { useAtomValue } from "jotai";
import { type FunctionComponent, useEffect, useMemo } from "react";

import { useArticleRenderContext } from "~/components/article/article-render/contexts";
import { useArticleSpeechInlineWidgetContext } from "~/components/article/article-speech/article-speech-inline-widget/contexts";
import { useArticleSpeechButton } from "~/components/article/article-speech/article-speech-inline-widget/hooks";
import type { VariantProps } from "~/components/article/article-speech/article-speech-inline-widget/variants/types";
import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { rosettaAtom } from "~/lib/rosetta";
import { ZIndexLayer } from "~/lib/styles";

import Pippa from "./img-pippa.png";
import {
  Container,
  Description,
  PippaImage,
  StyledButton,
  StyledLineImg,
  StyledPlayIcon,
  TextContainer,
  Title,
} from "./styles";

export const ArticleSpeechInlineWidgetPosties: FunctionComponent<VariantProps> = ({
  className,
}) => {
  const { setArticleSpeechifyPlayerConfig } = useArticleSpeechContext();
  const { isPlayed } = useArticleSpeechInlineWidgetContext();

  const asyncRosettaState = useAtomValue(rosettaAtom);

  const isShowForPostiesArticle = useMemo(() => {
    const shouldShowPaywall =
      asyncRosettaState?.status === "success" && !asyncRosettaState.result?.hasPostiesAccessRight;

    return !shouldShowPaywall;
  }, [asyncRosettaState?.result?.hasPostiesAccessRight, asyncRosettaState?.status]);

  const { articleElementReference } = useArticleRenderContext();
  const { isClicked, setIsClicked } = useArticleSpeechButton(true);

  const isButtonDisable = useMemo(
    () => isPlayed || !isShowForPostiesArticle || isClicked,
    [isClicked, isPlayed, isShowForPostiesArticle],
  );

  useEffect(() => {
    if (!isClicked || !articleElementReference?.current) return;

    setArticleSpeechifyPlayerConfig?.({
      customStyles: {
        playButton: {
          background: "radial-gradient(#ffffff 50%, transparent 50%)",
        },
        primaryColor: "#ff7c1d",
        widget: {
          borderRadius: "8px",
          zIndex: `${ZIndexLayer.Drawer - 1}`,
        },
      },
      enableAutoScroll: true,
      enableHighlighting: true,
      inlinePlayerElement: undefined,
      inlinePlayerSelector: undefined,
      paragraphSettings: {
        isEnabled: "while-playing",
        marginRight: "-10px",
      },
      rootElement: articleElementReference.current,
    });
  }, [articleElementReference, isClicked, setArticleSpeechifyPlayerConfig]);

  return (
    <Container className={className}>
      <PippaImage alt={Pippa.alt} src={Pippa.src} />
      <TextContainer>
        <Title>Listen Now</Title>
        <StyledLineImg />
        <Description>Enjoy the audio version of this article!</Description>
      </TextContainer>
      <StyledButton
        $isDisabled={isButtonDisable}
        backgroundColor={isButtonDisable ? "#cccccc" : "#fecc17"}
        variant="button"
        onClick={() => {
          if (isButtonDisable) return;
          setIsClicked();
        }}
      >
        <StyledPlayIcon />
        Play
      </StyledButton>
    </Container>
  );
};

ArticleSpeechInlineWidgetPosties.displayName = "ArticleSpeechInlineWidgetPosties";
